import { useEffect, useState, Fragment } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { GLDrillDialog, ReconciliationFooter } from "@/components/FinancialStatementShared";
import { ChevronRight, ChevronDown, TrendingUp } from "lucide-react";

const Row = ({ row, rowKey, onDrill, expanded, onToggle }) => (
  <Fragment>
    <tr className={`border-b hover:bg-slate-50 ${row.mapped === false ? "bg-amber-50" : ""}`} data-testid={`pl-row-${rowKey}`}>
      <td className="p-2 pl-6">
        {row.ledgers?.length > 0 ? (
          <button className="flex items-center gap-1 text-left hover:text-orange-700" onClick={onToggle} data-testid={`pl-expand-${rowKey}`}>
            {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            {row.schedule_head}
          </button>
        ) : row.schedule_head}
      </td>
      <td className="p-2 text-right tabular">{formatINR(row.current)}</td>
      <td className="p-2 text-right tabular text-slate-500">{row.previous ? formatINR(row.previous) : "—"}</td>
    </tr>
    {expanded && (row.ledgers || []).map((l, i) => (
      <tr key={i} className="border-b bg-slate-50/60 text-slate-600 cursor-pointer hover:bg-orange-50" onClick={() => onDrill(l)} data-testid={`pl-ledger-${rowKey}-${i}`}>
        <td className="p-1.5 pl-12 text-xs">{l.account_name} <span className="text-slate-400">· GL drill-down</span></td>
        <td className="p-1.5 text-right tabular text-xs">{formatINR(l.balance)}</td>
        <td className="p-1.5 text-right text-xs text-slate-300">·</td>
      </tr>
    ))}
  </Fragment>
);

export default function ProfitLossPage() {
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    api.get("/branches").then(r => setBranches(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    const qs = new URLSearchParams();
    if (dateFrom) qs.set("date_from", dateFrom);
    if (dateTo) qs.set("date_to", dateTo);
    if (branchId) qs.set("branch_id", branchId);
    api.get(`/financial-statements/profit-loss?${qs}`).then(r => setData(r.data)).catch(() => {});
  }, [dateFrom, dateTo, branchId]);

  if (!data) return <div className="text-slate-500 p-6" data-testid="pl-loading">Loading Profit & Loss…</div>;

  const t = data.totals;
  const p = t.previous;
  const toggle = k => setExpanded(prev => ({ ...prev, [k]: !prev[k] }));
  const sec = (rows, keyPrefix) => rows.map((r, i) => (
    <Row key={i} row={r} rowKey={`${keyPrefix}-${i}`} onDrill={setDrill}
         expanded={!!expanded[`${keyPrefix}-${i}`]} onToggle={() => toggle(`${keyPrefix}-${i}`)} />
  ));
  const totalRow = (label, cur, prev, testid) => (
    <tr className="bg-slate-100 font-bold" data-testid={testid}>
      <td className="p-2">{label}</td>
      <td className="p-2 text-right tabular">{formatINR(cur)}</td>
      <td className="p-2 text-right tabular text-slate-500">{formatINR(prev)}</td>
    </tr>
  );

  return (
    <div data-testid="profit-loss-page">
      <PageHeader
        eyebrow="Statutory Reporting"
        title="Profit & Loss — Schedule III"
        subtitle={`Statement of Profit and Loss · FY ${data.fy_code} · ${data.period.from} → ${data.period.to} · read directly from the verified General Ledger`}
        actions={
          <div className="flex items-center gap-2">
            <select value={branchId} onChange={e => setBranchId(e.target.value)} className="border border-slate-300 px-3 py-2 text-xs bg-white" data-testid="pl-branch-filter">
              <option value="">Consolidated (All Branches)</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name || b.branch_code || b.id}</option>)}
            </select>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="border border-slate-300 px-3 py-2 text-xs" data-testid="pl-from-filter" />
            <span className="text-xs text-slate-400">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border border-slate-300 px-3 py-2 text-xs" data-testid="pl-to-filter" />
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Total Revenue</div>
          <div className="font-display text-2xl font-black tabular mt-2" data-testid="pl-total-revenue">{formatINR(t.total_revenue)}</div>
        </Card>
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Total Expenses</div>
          <div className="font-display text-2xl font-black tabular mt-2" data-testid="pl-total-expenses">{formatINR(t.total_expenses)}</div>
        </Card>
        <Card className={`p-5 ${t.profit_before_tax >= 0 ? "border-emerald-300" : "border-red-300"}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2"><TrendingUp size={11} /> Profit Before Tax</div>
          <div className={`font-display text-2xl font-black tabular mt-2 ${t.profit_before_tax >= 0 ? "text-emerald-700" : "text-red-700"}`} data-testid="pl-pbt">
            {formatINR(t.profit_before_tax)}
          </div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b bg-white">
              <th className="p-2 text-[11px] uppercase tracking-wider">Particulars</th>
              <th className="p-2 text-right text-[11px] uppercase tracking-wider w-44">Current Period<br /><span className="normal-case font-normal">{data.period.from} → {data.period.to}</span></th>
              <th className="p-2 text-right text-[11px] uppercase tracking-wider w-44">Previous Period<br /><span className="normal-case font-normal">FY {data.previous_fy_code}</span></th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-slate-800 text-white"><td className="p-2 font-bold" colSpan={3}>I. Revenue from Operations</td></tr>
            {sec(data.sections.revenue_from_operations, "rev")}
            {totalRow("Total — Revenue from Operations", t.revenue_from_operations, p.revenue_from_operations, "pl-rev-total")}
            <tr className="bg-slate-800 text-white"><td className="p-2 font-bold" colSpan={3}>II. Other Income</td></tr>
            {data.sections.other_income.length === 0 && <tr className="border-b"><td className="p-2 pl-6 text-slate-400 text-xs" colSpan={3}>— Nil —</td></tr>}
            {sec(data.sections.other_income, "oi")}
            {totalRow("III. Total Revenue (I + II)", t.total_revenue, p.total_revenue, "pl-total-rev-row")}
            <tr className="bg-slate-800 text-white"><td className="p-2 font-bold" colSpan={3}>IV. Expenses</td></tr>
            {sec(data.sections.expenses, "exp")}
            {totalRow("Total Expenses", t.total_expenses, p.total_expenses, "pl-exp-total")}
            <tr className="bg-emerald-700 text-white font-bold" data-testid="pl-pbt-row">
              <td className="p-2">V. Profit Before Tax (III − IV)</td>
              <td className="p-2 text-right tabular">{formatINR(t.profit_before_tax)}</td>
              <td className="p-2 text-right tabular">{formatINR(p.profit_before_tax)}</td>
            </tr>
          </tbody>
        </table>
      </Card>

      <ReconciliationFooter recon={data.reconciliation} testid="pl-reconciliation" />
      <GLDrillDialog open={!!drill} onClose={() => setDrill(null)} account={drill} />
    </div>
  );
}
